import {
  DndContext,
  useSensor,
  useSensors,
  MouseSensor,
  TouchSensor,
} from "@dnd-kit/core";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "antd";
import { useSelector, useDispatch } from "react-redux";
import isEqual from "lodash.isequal";
import ResizeObserver from "resize-observer-polyfill";

import { Draggable } from "./Draggable";
import { Droppable } from "./Droppable";
import restaurantSchemaActions from "../../../redux/restaurantSchema/actions";
import CreateTableModal from "./CreateTableModal";
import EditTableModal from "./EditTableModal";
import AddObjectModal from "./AddObjectModal";

import "./index.css";

const VIRTUAL_WIDTH = 1300;
const VIRTUAL_HEIGHT = 650;

const { editReservationTable, getTouchsaleTablesPerRegion } =
  restaurantSchemaActions;

const RestaurantSchemeTablesForReservations = ({
  regionId,
  backgroundImage,
}) => {
  const dispatch = useDispatch();
  const [openCreateTableModal, setOpenCreateTableModal] = useState(false);
  const [openEditTableModal, setOpenEditTableModal] = useState(false);
  const [openAddObjectModal, setOpenAddObjectModal] = useState(false);
  const [clickedTable, setClickedTable] = useState();
  const { tables } = useSelector((state) => state.restaurantSchemaReducer);

  const [tablesData, setTablesData] = useState(tables);
  const prevTableCountRef = useRef(tables?.length ?? 0);

  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({
    width: VIRTUAL_WIDTH,
    height: VIRTUAL_HEIGHT,
  });

  useEffect(() => {
    // Fetch touchsale tables only on mount
    dispatch(
      getTouchsaleTablesPerRegion({
        organizationId: parseInt(localStorage.getItem("organizationId")),
        restaurantId: parseInt(localStorage.getItem("restaurantId")),
        regionId: regionId,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionId]);

  useEffect(() => {
    // Sync local state from Redux when tables are added or removed
    const currentCount = tables?.length ?? 0;
    const prevCount = prevTableCountRef.current;
    if (currentCount !== prevCount || !isEqual(tablesData, tables)) {
      setTablesData(tables);
      prevTableCountRef.current = currentCount;
      // Re-fetch touchsale tables only when count changes (add/delete)
      if (currentCount !== prevCount) {
        dispatch(
          getTouchsaleTablesPerRegion({
            organizationId: parseInt(localStorage.getItem("organizationId")),
            restaurantId: parseInt(localStorage.getItem("restaurantId")),
            regionId: regionId,
          })
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables]);

  useEffect(() => {
    let resizeTimer = null;
    const handleResize = (entries) => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        for (let entry of entries) {
          if (entry.target === containerRef.current) {
            const { width, height } = entry.contentRect;
            setContainerSize((prev) => {
              if (Math.abs(prev.width - width) < 2 && Math.abs(prev.height - height) < 2) {
                return prev;
              }
              return { width, height };
            });
          }
        }
      }, 100);
    };

    const resizeObserver = new ResizeObserver(handleResize);

    const currentContainer = containerRef.current;
    if (currentContainer) {
      resizeObserver.observe(currentContainer);
    }

    return () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      if (currentContainer) {
        resizeObserver.unobserve(currentContainer);
      }
    };
  }, []);

  const scaleFactor = Math.min(
    containerSize.width / VIRTUAL_WIDTH,
    containerSize.height / VIRTUAL_HEIGHT
  );

  function handleDragEnd(event) {
    const { id } = event.active;
    const { delta } = event;

    setTablesData((tables) =>
      tables.map((table) =>
        table.id === id
          ? {
              ...table,
              x: Math.max(
                0,
                Math.min(table.x + delta.x / scaleFactor, VIRTUAL_WIDTH - 70)
              ),
              y: Math.max(
                0,
                Math.min(table.y + delta.y / scaleFactor, VIRTUAL_HEIGHT - 70)
              ),
            }
          : table
      )
    );

    const updateTable = tablesData.find(
      (table) => table.id === id
    );
    if (!updateTable) return;

    dispatch(
      editReservationTable({
        x: Math.max(
          0,
          Math.min(updateTable.x + delta.x / scaleFactor, VIRTUAL_WIDTH - 70)
        ),
        y: Math.max(
          0,
          Math.min(updateTable.y + delta.y / scaleFactor, VIRTUAL_HEIGHT - 70)
        ),
        direction: updateTable.direction,
        scale: updateTable.scale || 1.0,
        shape: updateTable.shape,
        seatsCount: updateTable.seatsCount,
        tableName: updateTable.tableName,
        organizationId: parseInt(localStorage.getItem("organizationId")),
        touchSaleTableId: updateTable.touchSaleTableId,
        regionId: updateTable.regionId,
      })
    );
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 6,
      },
    })
  );

  const saveTimerRef = useRef({});

  const dispatchTableUpdate = useCallback(
    (table) => {
      dispatch(
        editReservationTable({
          x: table.x,
          y: table.y,
          direction: table.direction,
          scale: table.scale || 1.0,
          shape: table.shape,
          seatsCount: table.seatsCount,
          tableName: table.tableName,
          organizationId: parseInt(localStorage.getItem("organizationId")),
          touchSaleTableId: table.touchSaleTableId,
          regionId: table.regionId,
        })
      );
    },
    [dispatch]
  );

  const debouncedSave = useCallback(
    (id, updatedTable) => {
      if (saveTimerRef.current[id]) {
        clearTimeout(saveTimerRef.current[id]);
      }
      saveTimerRef.current[id] = setTimeout(() => {
        dispatchTableUpdate(updatedTable);
        delete saveTimerRef.current[id];
      }, 600);
    },
    [dispatchTableUpdate]
  );

  const handleScaleChange = useCallback(
    (id, newScale) => {
      setTablesData((prev) => {
        const updated = prev.map((t) =>
          t.id === id ? { ...t, scale: newScale } : t
        );
        const table = updated.find((t) => t.id === id);
        if (table) debouncedSave(id, table);
        return updated;
      });
    },
    [debouncedSave]
  );

  const handleRotateChange = useCallback(
    (id, newDirection) => {
      setTablesData((prev) => {
        const updated = prev.map((t) =>
          t.id === id ? { ...t, direction: newDirection } : t
        );
        const table = updated.find((t) => t.id === id);
        if (table) debouncedSave(id, table);
        return updated;
      });
    },
    [debouncedSave]
  );

  const handleDoubleClick = (id) => {
    setClickedTable(
      tablesData.filter((table) => table.id === id)
    );
    // Logic to open the modal or perform other actions
    setOpenEditTableModal(true);
  };

  return (
    <>
      <CreateTableModal
        openCreateTableModal={openCreateTableModal}
        setOpenCreateTableModal={setOpenCreateTableModal}
        regionId={regionId}
      />
      <EditTableModal
        openEditTableModal={openEditTableModal}
        setOpenEditTableModal={setOpenEditTableModal}
        clickedTable={clickedTable}
        setClickedTable={setClickedTable}
      />
      <AddObjectModal
        open={openAddObjectModal}
        setOpen={setOpenAddObjectModal}
        regionId={regionId}
      />
      <div className="reservations-schema-content-container">
        <DndContext onDragEnd={handleDragEnd} sensors={sensors}>
          <Droppable ref={containerRef} backgroundImage={backgroundImage}>
            {tablesData?.map((table) => (
              <Draggable
                key={table.id}
                id={table.id}
                tableName={table.tableName}
                position={{
                  x: table.x * scaleFactor,
                  y: table.y * scaleFactor,
                }}
                direction={table.direction}
                shape={table.shape}
                seatsCount={table.seatsCount}
                scale={table.scale || 1.0}
                scaleFactor={scaleFactor}
                busy={table.busy}
                nextReservation={table.nextReservation}
                onDoubleClick={handleDoubleClick}
                onScaleChange={handleScaleChange}
                onRotateChange={handleRotateChange}
              />
            ))}
          </Droppable>
        </DndContext>
      </div>
      <div className="reservations-schema-legend">
        <div className="reservations-schema-legend-item">
          <div className="reservations-schema-legend-dot free" />
          Свободна
        </div>
        <div className="reservations-schema-legend-item">
          <div className="reservations-schema-legend-dot busy" />
          Заета
        </div>
        <div className="reservations-schema-legend-item">
          <div className="reservations-schema-legend-dot reserved" />
          Резервирана
        </div>
      </div>
      <div className="reservations-schema-content-action-buttons">
        <Button onClick={() => setOpenCreateTableModal(true)}>
          Добави маса
        </Button>
        <Button onClick={() => setOpenAddObjectModal(true)}>
          Добави обект
        </Button>
      </div>
    </>
  );
};

export default RestaurantSchemeTablesForReservations;
